/**
 * LINE OA「再叫一桶」確認卡的「我要修改」入口（lpg-app-api docs/spec/app-line-oa-reorder-spec.md §六）。
 *
 * 後端把按鈕開成 `/kiosk?supplier=&shopCode=&oaDraft=<draft_id>`。OA 建的 draft 就是該 context 的
 * current draft：PageWrapper 的 useCurrentDraftSync 會把它載進購物車（useOrder），這裡只負責
 * 「載完之後、id 相符 → 直接轉 /kiosk/cart」，不另外打 API。
 *
 * 這支檔案只放可測的純函式；副作用（navigate、toast、改 store）在 useOaReorderEditEntry。
 */

/** 與後端 `OA_REORDER_EDIT_DRAFT_PARAM`（oa-reorder.constants.ts）同名，兩邊必須同步改。 */
export const OA_REORDER_EDIT_DRAFT_PARAM = "oaDraft";

/** 草稿已送出／被下一次再叫一桶刪舊建新／過期，或身分切到別的 context 時的提示。 */
export const OA_REORDER_EDIT_GONE_TEXT = "這張確認卡的內容已失效，請直接選購或重新點「再叫一桶」";

/** 只認本次 URL 的 `oaDraft`；draft_id 是 UUID，其餘字元一律濾掉，避免怪值進比對或埋點。 */
export const parseOaEditDraftId = (search: string): string | null => {
  const raw = new URLSearchParams(search).get(OA_REORDER_EDIT_DRAFT_PARAM) ?? "";
  const id = raw.replace(/[^A-Za-z0-9-]/g, "").slice(0, 64);
  return id === "" ? null : id;
};

/** 拿掉 `oaDraft`，其餘 query（supplier / shopCode 等）原樣保留。回傳含前導 `?` 或空字串。 */
export const stripOaEditDraftParam = (search: string): string => {
  const params = new URLSearchParams(search);
  params.delete(OA_REORDER_EDIT_DRAFT_PARAM);
  const rest = params.toString();
  return rest === "" ? "" : `?${rest}`;
};

export interface OaEditDraftState {
  /** URL 上的 oaDraft */
  expectedDraftId: string;
  /** kiosk bootstrap 載完（is_open_now / 配送時段 / 地址清單都已寫進 store） */
  bootstrapLoaded: boolean;
  /**
   * useCurrentDraftSync 已針對「目前這個 context」重新抓過 current draft。
   * 進頁時 orderState 可能是 sessionStorage 留下的舊 "success"，切身分時也會短暫沿用上一個 context
   * 的結果，所以只看 orderState 不夠——由呼叫端確認「看過這個 context 的 loading → success」。
   */
  draftSettled: boolean;
  currentDraftId: string | null;
  itemCount: number;
  /**
   * OA entry-check 寫的偏好身分還沒被身分選單套用（sessionStorage 仍有 oa_entry_preferred_context）。
   * 套用後身分會切換、草稿會重抓，這段期間的不相符不能當成失效。
   */
  preferredContextPending: boolean;
}

export type OaEditDraftDecision = "wait" | "open_cart" | "gone";

/**
 * - 還在載入 → wait
 * - current draft 就是卡上那張、且有品項 → open_cart（相符是決定性的，不必等偏好身分）
 * - 不相符但偏好身分還沒套用 → wait（切過去之後可能就相符了）
 * - 其餘 → gone
 */
export const decideOaEditDraft = (s: OaEditDraftState): OaEditDraftDecision => {
  if (!s.bootstrapLoaded || !s.draftSettled) return "wait";
  if (s.currentDraftId === s.expectedDraftId && s.itemCount > 0) return "open_cart";
  if (s.preferredContextPending) return "wait";
  return "gone";
};

/**
 * OA 建的 draft 一律 `immediate`（spec §5.2），但後端回的欄位是 `delivery_mode`，
 * useOrder.syncDraft 只認 `delivery_type` + `delivery_date`，立即配送的 draft 會沿用本地舊值
 *（可能是上一個 session 留下的預約時段）。這裡依 draft 與營業狀態明確決定要寫入的配送方式：
 * - `reserve` → 不動（syncDraft 已依 delivery_date 帶入）
 * - 其餘（immediate / null）→ 營業中 immediate；打烊（只能預約）→ scheduled 待客人選時段
 * 與 KioskHome 的一鍵重訂同一套規則。
 */
export const resolveOaEditDeliveryType = (
  draftDeliveryMode: string | null | undefined,
  isOpenNow: boolean,
): "immediate" | "scheduled" | null => {
  if (draftDeliveryMode === "reserve") return null;
  return isOpenNow ? "immediate" : "scheduled";
};

/**
 * 卡上的地址是 OA 依「上次配送地址」挑的（spec §5.2），不一定是 kiosk bootstrap 自動選的
 * addresses[0]；結算頁送單會用 choose_address 覆寫 draft 地址，所以要先把 choose_address
 * 對齊 draft。找不到（地址清單沒有那筆）→ null，維持現狀。
 */
export const findDraftAddress = <A extends { customer_address_id?: string | number }>(
  addresses: A[] | string | undefined,
  draftAddressId: string | number | null | undefined,
): A | null => {
  if (!Array.isArray(addresses) || draftAddressId == null) return null;
  return addresses.find((a) => String(a.customer_address_id) === String(draftAddressId)) ?? null;
};
